/**
 * Componente de Gráfico de Volume PGMQ
 * 
 * Exibe volume de mensagens processadas por hora (últimas 24h)
 * com barras para completados e falhas.
 */

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, CartesianGrid } from 'recharts';
import { useFlowQueueVolumePgmq } from '@/hooks/useFlowQueueMonitorPgmq';

// Configuração de cores do gráfico
const chartConfig = {
  completed: {
    label: 'Completados',
    color: 'hsl(var(--chart-2))',
  },
  failed: {
    label: 'Falhas',
    color: 'hsl(var(--chart-5))',
  },
};

export function PgmqVolumeChart() {
  const { data: volumeData, isLoading } = useFlowQueueVolumePgmq(24);

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Volume por Hora</CardTitle>
          <CardDescription>Últimas 24 horas</CardDescription>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-[300px] w-full" />
        </CardContent>
      </Card>
    );
  }

  // Formatar dados para o gráfico (últimas 12 horas para melhor visualização)
  const chartData = (volumeData || []).slice(-12).map((item) => ({
    hour: item.hour,
    completed: item.completed,
    failed: item.failed,
  }));

  return (
    <Card>
      <CardHeader>
        <CardTitle>Volume por Hora</CardTitle>
        <CardDescription>
          Mensagens processadas nas últimas 12 horas
        </CardDescription>
      </CardHeader>
      <CardContent>
        {chartData.length === 0 ? (
          <div className="h-[300px] flex items-center justify-center text-muted-foreground">
            Nenhum dado disponível
          </div>
        ) : (
          <ChartContainer config={chartConfig} className="h-[300px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis 
                  dataKey="hour" 
                  tick={{ fontSize: 12 }}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis 
                  tick={{ fontSize: 12 }}
                  tickLine={false}
                  axisLine={false}
                />
                <ChartTooltip content={<ChartTooltipContent />} />
                <Bar 
                  dataKey="completed" 
                  fill="var(--color-completed)" 
                  radius={[4, 4, 0, 0]}
                  name="Completados"
                />
                <Bar 
                  dataKey="failed" 
                  fill="var(--color-failed)" 
                  radius={[4, 4, 0, 0]}
                  name="Falhas"
                />
              </BarChart>
            </ResponsiveContainer>
          </ChartContainer>
        )}
      </CardContent>
    </Card>
  );
}
